import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectWaiterAuth, setLockedFrom, setUiLocked } from './waiterSlice';
import { useCartGuard } from './useCartGuard';
import { useIdleLock } from './useIdleLock';
import { useWaiterTokenRenewal } from './useWaiterTokenRenewal';
import { selectTableSession, selectGuestToken } from '@/features/tableSession/tableSessionSlice';
import { useGetTableSessionQuery } from '@/features/tableSession/tableSessionApi';
import LanguageSwitcher from '@/components/chrome/LanguageSwitcher';
import CartButton from '@/features/cart/CartButton';

interface Props {
  restaurantUuid: string;
  tableToken: string;
  tableNumber: string | number;
  /** Opens the "Ma table" panel (per-round order detail). The summary cluster
      taps through to it; RestaurantShell owns the panel's open state. */
  onOpenTablePanel: () => void;
}

function formatEuro(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

/**
 * Spec §3.1 — sticky top bar shown by RestaurantShell whenever the active
 * dine-in session was opened from waiter handheld mode (session.waiterMode).
 *
 * Consolidates the former three-row dine-in chrome (WaiterBar + SiteHeader +
 * MenuPage table banner) into ONE bar:
 *   "← Tables · Table N · N couverts · total · FR · 🛒 · 🔒".
 * Table / covers / running total are read from the live table-session poll.
 * TablePanel — always mounted during dine-in — drives that 5s poll, so this
 * subscription only reads the shared RTK-Query cache (no extra request). The
 * running total is hidden in buffet mode (buffet has no prices).
 *
 * Owns its own cart-guard dialog so "← Tables" never silently drops an unsent
 * round, and mounts the idle-lock timer (this is the ordering screen that
 * WaiterShell's own timer never sees). Because it replaces SiteHeader here, it
 * publishes its height as --site-header-h so the sticky CategoryPills sit flush.
 */
export default function WaiterBar({ restaurantUuid, tableToken, tableNumber, onOpenTablePanel }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectWaiterAuth);
  const session = useAppSelector(selectTableSession);
  const guestToken = useAppSelector(selectGuestToken);
  const { guardedNavigate, dialog } = useCartGuard(restaurantUuid);
  const barRef = useRef<HTMLDivElement>(null);

  const { data: currentData } = useGetTableSessionQuery(
    {
      restaurantUuid,
      sessionUuid: session?.sessionUuid ?? '',
      guestToken: guestToken ?? '',
    },
    { skip: !guestToken || !session },
  );

  const liveTableNumber = currentData?.table_number ?? tableNumber;
  const covers = currentData?.covers ?? session?.covers ?? 0;
  const isBuffet = (currentData?.mode ?? session?.mode) === 'buffet';
  const runningTotalCents = currentData?.running_total
    ? Math.round(parseFloat(currentData.running_total) * 100)
    : 0;

  // This bar replaces SiteHeader in waiter dine-in, so it must publish the
  // header height the sticky mobile CategoryPills offset off (--site-header-h).
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--site-header-h', `${el.offsetHeight}px`);
    update();
    if (typeof ResizeObserver === 'undefined') return () => root.style.removeProperty('--site-header-h');
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--site-header-h');
    };
  }, []);

  // Locking must PRESERVE the cart (cart is per-restaurant, survives the
  // route change) — no cart guard here, unlike "← Tables" below. Records the
  // served table so a successful re-PIN can return the waiter to it instead
  // of the bare tables grid (WaiterShell reads lockedFromTableToken).
  const lockAndReturnToTables = useCallback(() => {
    dispatch(setUiLocked(true));
    dispatch(setLockedFrom({ kind: 'table', tableToken }));
    navigate(`/${restaurantUuid}/serveur`);
  }, [dispatch, tableToken, restaurantUuid, navigate]);

  useIdleLock(auth?.idleLockSeconds, !!auth && !auth.uiLocked, lockAndReturnToTables);

  // SC-799 — un serveur poste sur une table tout un service ne repasse jamais
  // par WaiterShell : le renouvellement du jeton doit vivre ici aussi.
  useWaiterTokenRenewal(auth);

  const backToTables = () => {
    guardedNavigate(() => navigate(`/${restaurantUuid}/serveur`));
  };

  return (
    <>
      <div
        ref={barRef}
        data-testid="waiter-bar"
        className="sticky top-0 z-[600] bg-ink text-paper flex items-center justify-between gap-2 px-3 py-2 font-display text-sm"
      >
        <button
          type="button"
          onClick={backToTables}
          aria-label={t('waiter.bar.backToTables')}
          className="shrink-0 text-2xl leading-none bg-transparent border-0 appearance-none text-paper"
        >
          ←
        </button>

        {/* Table · covers · running total — the always-visible session summary,
            tappable to open the per-round detail panel. Total hidden in buffet. */}
        <button
          type="button"
          onClick={onOpenTablePanel}
          aria-label={t('waiter.bar.openPanel')}
          className="min-w-0 flex items-center justify-center gap-1.5 font-mono text-sm bg-transparent border-0 appearance-none text-paper"
        >
          <span className="whitespace-nowrap">{t('waiter.bar.tableLabel', { number: liveTableNumber })}</span>
          <span aria-hidden="true" className="opacity-50">·</span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <CoversIcon className="w-4 h-4" />
            {covers}
          </span>
          {!isBuffet && runningTotalCents > 0 && (
            <>
              <span aria-hidden="true" className="opacity-50">·</span>
              <span className="whitespace-nowrap font-bold">{formatEuro(runningTotalCents)}</span>
            </>
          )}
        </button>

        <div className="shrink-0 flex items-center gap-1.5">
          <LanguageSwitcher />
          <CartButton restaurantUuid={restaurantUuid} variant="compact" />
        </div>
      </div>
      {dialog}
    </>
  );
}

// Two-figure "covers" glyph (currentColor stroke → inherits the bar's paper text).
function CoversIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
